import { t } from "i18next";
import { toast } from "sonner";

import { handleHttpError } from "@/Utils/request/errorHandler";
import { HTTPError } from "@/Utils/request/types";

type DuplicateFileErrorEntry = {
  type: string;
  field: string;
  duplicate_field: string;
  msg: string;
};

function isDuplicateFileErrors(
  errors: unknown,
): errors is DuplicateFileErrorEntry[] {
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every(
      (error) =>
        error &&
        typeof error === "object" &&
        (error as { type?: string }).type === "duplicate_files_detected",
    )
  );
}

// Maps the backend's file field paths (see PATIENT_FILE_FIELDS /
// INSURANCE_FILE_FIELD in care/emr/api/viewsets/patient.py) to the
// translation keys used for these fields' labels in the registration form.
const PATIENT_FILE_FIELD_LABEL_KEYS: Record<string, string> = {
  patient_photo_file: "patient_photo",
  id_proof_document_file: "id_proof_document",
  abha_id_document_file: "abha_id_document",
};

const INSURANCE_FILE_FIELD_PATTERN =
  /^insurance_records\.\d+\.insurance_card_letter_file$/;

function getDuplicateFileFieldLabel(fieldPath: string): string {
  if (PATIENT_FILE_FIELD_LABEL_KEYS[fieldPath]) {
    return t(PATIENT_FILE_FIELD_LABEL_KEYS[fieldPath]);
  }
  if (INSURANCE_FILE_FIELD_PATTERN.test(fieldPath)) {
    return t("insurance_document");
  }
  // Fallback for any future file field without a known label yet.
  return fieldPath;
}

function showDuplicateFileToasts(errors: DuplicateFileErrorEntry[]) {
  errors.forEach(({ field, duplicate_field }) => {
    const file = getDuplicateFileFieldLabel(field);
    const duplicate = duplicate_field
      ? getDuplicateFileFieldLabel(duplicate_field)
      : t("document");

    toast.error(t("duplicate_patient_registration_file", { file, duplicate }));
  });
}

/**
 * Error handler for the Patient Registration create/update mutations.
 *
 * Gives duplicate_files_detected errors a field-aware message
 * ("X matches Y. Please choose a different document."). Every other
 * error is delegated to the app's default handler so nothing else
 * about existing validation-error behavior changes.
 */
export function handlePatientRegistrationMutationError(error: unknown) {
  if (error instanceof HTTPError) {
    const errs = (error.cause as { errors?: unknown } | undefined)?.errors;

    if (isDuplicateFileErrors(errs)) {
      showDuplicateFileToasts(errs);
      return;
    }
  }

  handleHttpError(error as Error);
}
